import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDrivers } from "@/hooks/useDrivers";
import { useDriverSummary } from "@/hooks/useRepayments";
import { ComboboxSelect } from "@/components/form/combobox-select";
import { DataTable } from "@/components/ui/data-table";
import { RepaymentTypeBadge as TypeBadge, RepaymentStatusBadge } from "@/components/ui/repayment-badges";
import { formatRepaymentCode, formatFreightCode } from "@/lib/utils";
import { getRepaymentDisplayStatus } from "@/lib/repayment-status";
import type { Repayment } from "@/types/repayment";
import type { Column } from "@/components/ui/data-table";

function formatMoney(value: string | number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value));
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
}

interface SummaryCardProps {
  label: string;
  value: string;
  count?: number;
  variant?: "default" | "pending" | "paid";
}

function SummaryCard({ label, value, count, variant = "default" }: SummaryCardProps) {
  const color =
    variant === "pending"
      ? "border-yellow-200 bg-yellow-50"
      : variant === "paid"
        ? "border-green-200 bg-green-50"
        : "border-border bg-muted/40";

  const textColor =
    variant === "pending"
      ? "text-yellow-700"
      : variant === "paid"
        ? "text-green-700"
        : "text-foreground";

  return (
    <div className={`rounded-lg border p-4 ${color}`}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className={`mt-1 text-xl font-bold ${textColor}`}>{formatMoney(value)}</p>
      {count !== undefined && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          {count} repasse{count !== 1 ? "s" : ""}
        </p>
      )}
    </div>
  );
}

const COLUMNS: Column<Repayment>[] = [
  {
    key: "code",
    header: "Código",
    className: "w-20",
    render: (row) => (
      <span className="font-mono text-xs font-medium text-foreground">
        {formatRepaymentCode(row.code)}
      </span>
    ),
  },
  {
    key: "type",
    header: "Tipo",
    render: (row) => <TypeBadge type={row.type} />,
  },
  {
    key: "freight",
    header: "Frete",
    render: (row) => (
      <span>
        <span className="mr-1.5 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
          {formatFreightCode(row.freight.code)}
        </span>
        {row.freight.originCity}/{row.freight.originState} → {row.freight.destinationCity}/{row.freight.destinationState}
      </span>
    ),
  },
  {
    key: "amount",
    header: "Valor",
    render: (row) => (
      <span className="font-medium">{formatMoney(row.amount)}</span>
    ),
  },
  {
    key: "status",
    header: "Status",
    render: (row) => (
      <RepaymentStatusBadge status={getRepaymentDisplayStatus(row)} />
    ),
  },
  {
    key: "payment",
    header: "Data pag.",
    render: (row) =>
      row.payment ? formatDate(row.payment.paymentDate) : "—",
  },
  {
    key: "createdAt",
    header: "Criado em",
    render: (row) => formatDate(row.createdAt),
  },
];

export function RepaymentConsolidatedPage() {
  const [searchParams] = useSearchParams();
  const [driverId, setDriverId] = useState(
    searchParams.get("driverId") ?? "",
  );

  const { data: driversData } = useDrivers({ active: true, perPage: 200 });
  const driverOptions = [
    { value: "", label: "Selecione um motorista…" },
    ...(driversData?.data ?? []).map((d) => ({ value: d.id, label: d.name })),
  ];

  const { data: summary, isLoading } = useDriverSummary(driverId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">
          Consolidado por Motorista
        </h1>
        <p className="text-sm text-muted-foreground">
          Visão geral de repasses de um motorista específico
        </p>
      </div>

      <ComboboxSelect
        options={driverOptions}
        value={driverId}
        onChange={setDriverId}
        placeholder="Selecione um motorista…"
        searchPlaceholder="Buscar motorista..."
        className="w-full sm:w-72"
      />

      {!driverId && (
        <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-border">
          <p className="text-sm text-muted-foreground">
            Selecione um motorista para ver o consolidado
          </p>
        </div>
      )}

      {driverId && isLoading && (
        <div className="flex h-48 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}

      {summary && (
        <>
          <div>
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {summary.driver.name}
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <SummaryCard
                label="Total pendente"
                value={summary.summary.totalPending}
                count={summary.summary.countPending}
                variant="pending"
              />
              <SummaryCard
                label="Total pago"
                value={summary.summary.totalPaid}
                count={summary.summary.countPaid}
                variant="paid"
              />
              <SummaryCard
                label="Adiant. pendente"
                value={summary.summary.advancePending}
                variant="pending"
              />
              <SummaryCard
                label="Adiant. pago"
                value={summary.summary.advancePaid}
                variant="paid"
              />
              <SummaryCard
                label="Saldo pendente"
                value={summary.summary.balancePending}
                variant="pending"
              />
              <SummaryCard
                label="Saldo pago"
                value={summary.summary.balancePaid}
                variant="paid"
              />
            </div>
          </div>

          <DataTable
            columns={COLUMNS}
            data={summary.repayments}
            keyExtractor={(row) => row.id}
            isLoading={false}
            emptyTitle="Nenhum repasse encontrado"
            emptyDescription="Este motorista ainda não possui repasses registrados."
          />
        </>
      )}
    </div>
  );
}
